import { Component, OnInit } from '@angular/core';
import { alert } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AppInformationService, RAFUnitOfWork } from 'src/app/core/core.module';
import { Employee, Role, User } from 'src/app/core/data/entities';
import * as _ from 'lodash';

@Component({
    selector: 'app-user-detail-dialog',
    templateUrl: './user-detail-dialog.component.html',
    styleUrls: ['./user-detail-dialog.component.scss'],
    standalone: false
})
export class UserDetailDialogComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    userId: number;
    user: any;
    validationGroup = 'mainValidationGroup';
    errorAction = 'saving';
    readOnly = false as boolean;
    filter: any;
    processing = false;
    isToastVisible = false;
    usernameValidationMessage = 'Username is already in use';

    roles: Role[];
    currentRoleIds: number[] = [];
    selectedRoleIds: number[] = [];

    employees: Employee[];
    mode: string;

    constructor(public modalRef: BsModalRef, private uow: RAFUnitOfWork, private appInfo: AppInformationService) {}

    async ngOnInit() {
        this.roles = (await this.uow.roles.all()) as Role[];

        this.filter = {};
        this.employees = (await this.uow.getEmployees()).filter((x) => {
            return x.id !== 1 && !x.endDate;
        });
        await this.getData();
        this.filter.linkedEmployeeId = this.user.employeeId;
    }

    async getData() {
        if (!this.userId) {
            this.user = this.uow.users.createEntity();
        } else {
            this.user = (await this.uow.users.byId(this.userId, 'userRoles')) as any as User;
            // this.user.employeeId = null;
            const employee = _.find(this.employees, { userId: this.user.id });
            this.user.employeeId = !!employee ? employee.id : null;
        }
        this.user.newPassword = '';

        this.currentRoleIds = this.user?.userRoles.map((userRoles) => userRoles.roleId) ?? [];
        this.selectedRoleIds = this.currentRoleIds.slice();
    }

    async save() {
        if (!(await this.validate())) return;
        const employee = (await this.uow.employees.byId(this.filter.linkedEmployeeId)) as any;
        if (!employee) {
            alert('No Employee Record found.', `${this.appInfo.settings.company}`);
            return;
        }
        await this.uow.commit();
        await this.updateRoles();
        employee.userId = this.user.id;
        await this.uow.commit();
        if (this.user.newPassword) {
            this.uow.changeUserPassword(this.user, this.user.newPassword);
        }

        this.result.next({ success: true, value: this.user });
    }

    async updateRoles() {
        const addedRoleIds = _.difference(this.selectedRoleIds, this.currentRoleIds);
        const removedRoleIds = _.difference(this.currentRoleIds, this.selectedRoleIds);

        addedRoleIds.forEach((roleId) => {
            this.uow.userRoles.createEntity({
                roleId: roleId,
                userId: this.user.id
            });
        });

        removedRoleIds.forEach((roleId) => {
            const userRole = this.user.userRoles.find((userRole) => userRole.roleId === roleId);
            if (userRole) userRole.entityAspect.setDeleted();
        });
    }

    close() {
        this.uow.rollback();
        this.result.next({ success: false });
    }

    async validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            const completePromise = result.complete as any;
            if (completePromise && result.status === 'pending') {
                const asyncResult = await completePromise;
                return this.isValidWithMessage(asyncResult);
            } else {
                return this.isValidWithMessage(result);
            }
        } else {
            return true;
        }
    }

    private isValidWithMessage(result) {
        if (!result.isValid) {
            let message = '';
            result.brokenRules.forEach((brokenRule) => {
                if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
            });
            this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
            return false;
        } else {
            return true;
        }
    }

    validateUsername = async (e) => {
        // check if this username is already used...
        const users = await this.uow.getUserInfos({ notId: this.user.id, userName: this.user.userName });
        return users.length === 0;
    };

    validatePassword = (e) => {
        if (!this.user.newPassword || this.user.newPassword.trim() === '') {
            return true;
        } else {
            return this.user.newPassword.length >= 8;
        }
    };

    validatePasswordRequired = (e) => {
        return this.user.id > 0 || (!!this.user.newPassword && this.user.newPassword.trim() !== '');
    };
}
